// Atividade dos usuários: em que tela estão e quanto tempo passam em cada uma.
// Só conta tempo com a aba visível e com uso recente (sem 5 min parado).
import { useEffect, useRef } from "react";
import { sb } from "./supabase";
import { NAV } from "./constants";

const IDLE_MS = 5 * 60 * 1000;
const FLUSH_MS = 30 * 1000;
let disabled = false;   // função ainda não existe no banco: para de tentar nesta sessão

function send(screen, seconds, enter, device) {
  if (disabled) return;
  sb.rpc("log_activity", { p_screen: screen, p_seconds: seconds, p_enter: !!enter, p_device: device || null })
    .then(({ error }) => { if (error && (error.code === "PGRST202" || error.code === "42883")) disabled = true; })
    .catch(() => {});
}

export function useActivityTracker(uid, screen, device) {
  const st = useRef({ screen: null, since: null, lastInput: Date.now() }).current;

  const flush = () => {
    if (!st.screen || !st.since) return;
    const now = Date.now();
    const end = Math.min(now, st.lastInput + IDLE_MS);
    const sec = Math.round((end - st.since) / 1000);
    st.since = now;
    if (sec > 0) send(st.screen, Math.min(sec, 120), false);
  };

  // Troca de tela: fecha o tempo da anterior e registra a entrada na nova
  useEffect(() => {
    if (!uid || !screen) return;
    flush();
    st.screen = screen === "admin" ? null : screen;
    st.lastInput = Date.now();
    st.since = document.visibilityState === "visible" ? Date.now() : null;
    if (st.screen) send(st.screen, 0, true, device);
  }, [uid, screen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!uid) return;
    const onInput = () => {
      const now = Date.now();
      if (st.since && now - st.lastInput > IDLE_MS) st.since = now;   // voltou de uma pausa: não conta o tempo parado
      st.lastInput = now;
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") { flush(); st.since = null; }
      else { st.since = Date.now(); st.lastInput = Date.now(); }
    };
    const iv = setInterval(() => { if (document.visibilityState === "visible") flush(); }, FLUSH_MS);
    const evs = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];
    evs.forEach(e => window.addEventListener(e, onInput, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    return () => {
      flush();
      st.screen = null; st.since = null;
      clearInterval(iv);
      evs.forEach(e => window.removeEventListener(e, onInput, { capture: true }));
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
    };
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ── Painel do administrador ──────────────────────────────────────────────────
const EXTRA_LABELS = { admin: "Admin" };
export const screenLabel = id => (NAV.find(n => n.id === id) || {}).label || EXTRA_LABELS[id] || id || "—";

export async function dbGetActivity(days) {
  const { data, error } = await sb.rpc("get_admin_activity", { p_days: days });
  if (error) throw error;
  return data || { users: [], days: [] };
}

export async function dbGetTimeline(userId, limit = 80) {
  const { data, error } = await sb.rpc("get_admin_user_timeline", { p_user: userId, p_limit: limit });
  if (error) throw error;
  return data || [];
}

export function fmtDur(sec) {
  const s = Math.round(sec || 0);
  if (s < 60) return s > 0 ? "<1 min" : "0 min";
  const m = Math.round(s / 60);
  if (m < 60) return m + " min";
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h${String(r).padStart(2, "0")}` : `${h}h`;
}
